import styled from 'styled-components';
import cls from 'classnames';

import { Bbo } from '@paradigm/features/src/unified-rfqs/entities/drfq/bbo';
import { GrfqBbo } from '@paradigm/features/src/unified-rfqs/entities/grfq/bbo';
import {
  formatDelta,
  formatGamma,
  formatTheta,
  formatVega,
} from '@paradigm/features/src/unified-rfqs/entities/greeks';
import GridCellBase, {
  GridCellProps,
} from '#/components/UnifiedRfqs/components/GridCellBase';
import { MOBILE_BREAKPOINT } from '#/containers/layout/constants';

interface StrategyGreeks {
  readonly bbo: Pick<Bbo | GrfqBbo, 'greeks'> | null;
  readonly hideOnMobile?: boolean;
  readonly venue: string;
  readonly height?: number;
  readonly className?: string;
}

export function StrategyGreeksHeaderRow() {
  return (
    <Row>
      <HeaderCell>Delta (∆)</HeaderCell>
      <HeaderCell>Gamma (Γ)</HeaderCell>
      <HeaderCell>Vega (V)</HeaderCell>
      <HeaderCell>Theta (Θ)</HeaderCell>
    </Row>
  );
}

export function StrategyGreeksRow({ bbo, height }: StrategyGreeks) {
  const greeks = bbo?.greeks;
  return (
    <Row
      css={`
        height: ${height != null ? `${height}px` : 'auto'};
      `}
    >
      <Cell data-testid="delta">{formatDelta(greeks?.delta)}</Cell>
      <Cell data-testid="gamma">{formatGamma(greeks?.gamma)}</Cell>
      <Cell data-testid="vega">{formatVega(greeks?.vega)}</Cell>
      <Cell data-testid="theta">{formatTheta(greeks?.theta)}</Cell>
    </Row>
  );
}

export default function StrategyGreeks({
  bbo,
  className,
  hideOnMobile = true,
}: StrategyGreeks) {
  const greeks = bbo?.greeks;
  return (
    <Container className={className} mobile={!hideOnMobile}>
      <Title>Greeks (Unhedged)</Title>
      <Row mobile={!hideOnMobile}>
        <Cell>Delta (∆)</Cell>
        <Cell data-testid="delta">{formatDelta(greeks?.delta)}</Cell>
      </Row>
      <Row mobile={!hideOnMobile}>
        <Cell>Gamma (Γ)</Cell>
        <Cell data-testid="gamma">{formatGamma(greeks?.gamma)}</Cell>
      </Row>
      <Row mobile={!hideOnMobile}>
        <Cell>Vega (V)</Cell>
        <Cell data-testid="vega">{formatVega(greeks?.vega)}</Cell>
      </Row>
      <Row mobile={!hideOnMobile}>
        <Cell>Theta (Θ)</Cell>
        <Cell data-testid="theta">{formatTheta(greeks?.theta)}</Cell>
      </Row>
    </Container>
  );
}

const Cell = styled.span.attrs({ className: 'strategy-greeks-cell' })`
  font-size: inherit;
  line-height: inherit;
`;

const HeaderCell = styled.span`
  font-size: inherit;
`;

const Row = styled(GridCellBase).attrs<GridCellProps>(() => ({
  area: 'greeks',
}))<GridCellProps>`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 0.25rem;
  width: 100%;
  align-items: center;
`;

const Container = styled(GridCellBase).attrs((props) => {
  cls('strategy-greeks', props.className);
})`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  width: 100%;
  gap: 0.625rem;

  ${Row} {
    gap: 0.5rem;
    grid-template-columns: repeat(2, 1fr);
  }

  ${Cell} {
    font-size: 0.75rem;

    &:first-child {
      opacity: 0.5;
      color: white;
      font-size: 0.75rem;
      font-weight: bold;
      line-height: 1rem;
    }

    &:last-child {
      color: white;
      font-weight: 500;
      line-height: 0.875rem;
      text-align: right;
    }

    &:last-child,
    &:first-child {
      @media (max-width: ${MOBILE_BREAKPOINT}px) {
        font-size: 0.875rem;
        font-weight: 500;
        padding-bottom: 0.25rem;
      }
    }
  }

  ${HeaderCell} {
    opacity: 0.5;
    font-size: 0.75rem;
    font-weight: bold;
    line-height: 1rem;
  }
`;

const Title = styled.div.attrs({ className: 'strategy-greeks-title' })`
  color: white;
  font-size: 0.75rem;
  font-weight: 500;
  line-height: 1rem;
  padding-bottom: 0.125rem;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    font-size: 1rem;
    padding: 0.5rem 0;
  }
`;
